import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { Observable, Subscription } from "rxjs";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PlaysService } from "src/app/shared/services/plays/plays.service";
import { environment } from "src/environments/environment";
import { setServers } from "dns";

interface Track {
  title: string;
  id?: string;
  playCount?: number;
  uniqueListenerCount?: number;
  editing?: boolean;
  url: string;
  price?: number;
  membersOnly?: boolean;
  tier?: string;
  level?: string;
}

interface Album {
  art: string;
  artist: string;
  id: string;
  name: string;
  released: string;
  showing: boolean;
  tracks: Track[];
  type: string;
  editing?: boolean;
  mode?: string;
  price?: number;
  copied?: boolean;
  membersOnly?: boolean;
  tier?: string;
  level?: string;
}

interface ResultTrack {
  playCount: number;
  uniqueListeners: Set<string>; // Assuming it's a set of user IDs
}

interface ResultAlbum {
  art: string;
  totalPlays: number;
  tracks: { [trackName: string]: ResultTrack };
}

@Component({
  selector: "app-stats",
  templateUrl: "./stats.component.html",
  styleUrls: ["./stats.component.scss"],
})
export class StatsComponent implements OnInit, OnDestroy {
  albums: any[] = [];
  videos: any[] = [];
  albumForm: FormGroup;
  editingAlbumId: string | null = null;
  discogData: any;
  upload: number;
  uploadProgress$: Observable<{ progress: number; song: string }> | undefined;
  mode: string = "";
  songUrl: string;
  creatorId: string;
  @Input() user: any;
  currentUser: any;
  currentView:
    | "music"
    | "video"
    | "engagement"
    | "concerts"
    | "memberships"
    | "profile" = "engagement";

  // Data for ngx-charts
  subscriberFollowerData: any[];
  revenueBreakdownData: any[];
  topSongs: any[];
  topVideos: any[];

  get hasEngagementData(): boolean {
    // Check if the data array exists and has items.
    // This is sufficient now that we are always passing the series to the chart.
    return (
      this.subscriberFollowerData && this.subscriberFollowerData.length > 0
    );
  }

  // Concerts Data
  upcomingConcerts: any[] = [];
  pastConcerts: any[] = [];
  concertForm: FormGroup;
  isEditingConcert = false;
  showConcertForm = false;

  // Memberships Data
  membershipForm: FormGroup;
  isEditingMembership = false;
  showMembershipForm = false;
  editingMembershipIndex: number | null = null;
  canSetConcertPrice = false;

  // Profile Data
  profileForm: FormGroup;
  vanityUrl: string;
  hasFullAnalyticsAccess = false;
  canEditHandle = false;
  handleExistsError = false;
  selectedAvatarFile: File | null = null;
  selectedBannerFile: File | null = null;
  avatarPreviewUrl: string | ArrayBuffer | null = null;
  bannerPreviewUrl: string | ArrayBuffer | null = null;
  avatarUploadProgress: number | null = null;
  bannerUploadProgress: number | null = null;
  selectedTrackFile: File | null = null;
  private uiSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private playDataService: PlaysService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private _snackBar: MatSnackBar
  ) {
    this.albumForm = this.fb.group({
      name: "",
      artist: "",
      type: "",
      art: "",
      released: "",
      tracks: this.fb.array([]),
    });
    this.initializeConcertForm();
    this.initializeMembershipForm();
    this.initializeProfileForm();
  }

  copyShareLink(album: Album) {
    // The protocol is added to make it a full, clickable URL
    const shareLink = `https://www.apps4artists.uk/${this.currentUser.id}/share/${album.id}`;
    navigator.clipboard.writeText(shareLink).then(() =>
      this._snackBar.open("Link copied to clipboard!", "Close", {
        duration: 2000,
      })
    );
  }
  initializeConcertForm(concert: any = null) {
    const concertData = concert || {}; // Ensure we have an object to work with
    this.concertForm = this.fb.group({
      id: [concertData.id || null],
      venue: [concertData.venue || "", Validators.required],
      city: [concertData.city || "", Validators.required],
      country: [concertData.country || "", Validators.required],
      date: [
        concertData.date ? this.formatDateForInput(concertData.date) : "",
        Validators.required,
      ],
      capacity: [concertData.capacity || 0, Validators.required],
      status: [concertData.status || "on-sale", Validators.required],
      ticketTypes: this.fb.array(
        concertData.ticketTypes
          ? concertData.ticketTypes.map((t) => this.createTicketTypeGroup(t))
          : []
      ),
    });
  }

  createTicketTypeGroup(ticket: any = {}): FormGroup {
    // Disable price control if user is not allowed to set it
    const priceControl = this.fb.control(
      { value: ticket.price || 0, disabled: !this.canSetConcertPrice },
      Validators.required
    );

    return this.fb.group({
      id: [ticket.id || "ga"],
      name: [ticket.name || "General Admission", Validators.required],
      price: priceControl,
      description: [ticket.description || ""],
      // ticketsSold is a tracked stat, not part of the form
      ticketsSold: [ticket.ticketsSold || 0],
    });
  }

  initializeMembershipForm(membership: any = {}) {
    this.membershipForm = this.fb.group({
      tier: [membership.tier || "", Validators.required],
      price: [membership.price || 0, [Validators.required, Validators.min(0)]],
      description: [membership.description || "", Validators.required],
      level: [
        { value: membership.level || "", disabled: true },
        Validators.required,
      ],
      popular: [membership.popular || false],
      features: this.fb.array(
        membership.features
          ? membership.features.map((f) =>
              this.fb.control(f, Validators.required)
            )
          : []
      ),
    });
  }

  initializeProfileForm(user: any = {}) {
    console.log(user);
    // Safely get social links, handling both the old array format and the new map format.
    const socialLinks =
      (Array.isArray(user.socialLinks)
        ? user.socialLinks[0]
        : user.socialLinks) || {};

    this.profileForm = this.fb.group({
      handle: [user.handle || ""],
      shortBio: [user.shortBio || ""],
      fanClub: [user.fanClub || ""],
      featuredVideo: [user.featureVideo || ""],
      socialLinks: this.fb.group({
        appleMusic: [socialLinks.appleMusic || ""],
        spotify: [socialLinks.spotify || ""],
        youtube: [socialLinks.youtube || ""],
      }),
      extraLinks: this.fb.array(
        user.extraLinks
          ? user.extraLinks.map((link) => this.createExtraLinkGroup(link))
          : []
      ),
    });
  }

  createExtraLinkGroup(link: any = {}): FormGroup {
    return this.fb.group({
      title: [link.title || "", Validators.required],
      url: [link.url || "", Validators.required],
    });
  }

  toggleMode(event: any, album) {
    album.mode = event.checked ? "Active" : "Draft";
    this.playDataService.updateAlbum(album.id, {
      showing: event.checked,
    });
  }

  toggleVideoMode(event: any, video: any) {
    video.mode = event.checked ? "Active" : "Draft";
    this.playDataService.updateVideo(video.id, {
      showing: event.checked,
    });
  }

  deleteAlbum(albumId: string) {
    // Use the browser's confirm dialog for a simple confirmation
    if (
      confirm(
        "Are you sure you want to delete this release? This action cannot be undone."
      )
    ) {
      this.playDataService
        .deleteAlbum(albumId)
        .then(() => {
          // On successful deletion, remove the album from the local array to update the UI
          this.albums = this.albums.filter((album) => album.id !== albumId);
          this.cdr.detectChanges(); // Notify Angular that the data has changed
          console.log("Album deleted successfully.");
        })
        .catch((error) => console.error("Error deleting album:", error));
    }
  }

  deleteVideo(videoId: string) {
    if (
      confirm(
        "Are you sure you want to delete this video? This action cannot be undone."
      )
    ) {
      this.playDataService
        .deleteVideo(videoId)
        .then(() => {
          this.videos = this.videos.filter((video) => video.id !== videoId);
          this.cdr.detectChanges();
          console.log("Video deleted successfully.");
        })
        .catch((error) => console.error("Error deleting video:", error));
    }
  }

  async ngOnInit(): Promise<void> {
    this.uiSubscription = this.authService.ui$.subscribe(async (user) => {
      this.currentUser = user;
      if (this.currentUser) {
        const canEditHandleTypes = ["verified", "premium", "label"];
        this.canEditHandle = canEditHandleTypes.includes(
          this.currentUser.creatorType
        );
        this.hasFullAnalyticsAccess = canEditHandleTypes.includes(
          this.currentUser.creatorType
        );
        this.canSetConcertPrice = true; // All creator types can now set concert prices

        // Build Vanity URL
        const handle = this.currentUser.handle;
        this.vanityUrl =
          this.canEditHandle && handle
            ? `www.apps4artists.uk/@${handle}`
            : `www.apps4artists.uk/${this.currentUser.id}`;

        // Initialize Profile Form
        this.initializeProfileForm(this.currentUser);
        if (!this.canEditHandle) {
          this.profileForm.get("handle").disable();
        }

        // Load all album/video metadata first to prevent race conditions
        await this.loadAllStats();
        // Then load the engagement data which depends on the metadata
        this.loadEngagementData();
        this.loadConcerts();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.uiSubscription) {
      this.uiSubscription.unsubscribe();
    }
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      // Check for .jpg or .jpeg file types
      if (file.type !== "image/jpeg") {
        alert("Please select a .jpg or .jpeg file for your avatar.");
        // Reset the input so the user can select a new file
        input.value = "";
        this.selectedAvatarFile = null;
        this.avatarPreviewUrl = null;
        return;
      }
      this.selectedAvatarFile = file;
      const reader = new FileReader();
      reader.onload = () => (this.avatarPreviewUrl = reader.result);
      reader.readAsDataURL(this.selectedAvatarFile);
    }
  }

  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      // Check for .jpg or .jpeg file types
      if (file.type !== "image/jpeg") {
        alert("Please select a .jpg or .jpeg file for your banner.");
        input.value = "";
        this.selectedBannerFile = null;
        this.bannerPreviewUrl = null;
        return;
      }
      this.selectedBannerFile = file;
      const reader = new FileReader();
      reader.onload = () => (this.bannerPreviewUrl = reader.result);
      reader.readAsDataURL(this.selectedBannerFile);
    }
  }

  navigateToUpgrade() {
    this.router.navigate(["/pricing"]); // Assuming /pricing is your upgrade page route
  }

  get ticketTypes(): FormArray {
    return this.concertForm.get("ticketTypes") as FormArray;
  }

  get features(): FormArray {
    return this.membershipForm.get("features") as FormArray;
  }

  get extraLinks(): FormArray {
    return this.profileForm.get("extraLinks") as FormArray;
  }

  addExtraLink() {
    this.extraLinks.push(this.createExtraLinkGroup());
  }

  removeExtraLink(index: number) {
    this.extraLinks.removeAt(index);
  }

  addFeature() {
    this.features.push(this.fb.control("", Validators.required));
  }

  removeFeature(index: number) {
    this.features.removeAt(index);
  }

  addTicketType() {
    this.ticketTypes.push(this.createTicketTypeGroup());
  }

  removeTicketType(index: number) {
    this.ticketTypes.removeAt(index);
  }

  formatDateForInput(dateStr: string): string {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const localISO = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    ).toISOString();
    return localISO.slice(0, 16);
  }
  // Formats the chart tooltip values to display as currency.
  dollarValueFormat(value: any): string {
    console.log("Formatting value for tooltip:", value); // Debugging: Check what value is received
    if (typeof value === "number") {
      return `$${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    console.warn("Value passed to dollarValueFormat is not a number:", value); // Debugging: Warn if not a number
    return String(value); // Fallback for non-numeric values
  }

  async loadEngagementData() {
    if (!this.currentUser?.id) return;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const [
      subscriptionPayments,
      sales,
      streams,
      videoViews,
      pageViews, // new
    ] = await Promise.all([
      this.playDataService.getSubscriptionPaymentsByCreator(
        this.currentUser.id,
        thirtyDaysAgo
      ),
      this.playDataService.getSalesByCreator(
        this.currentUser.id,
        thirtyDaysAgo
      ),
      this.playDataService.getStats(thirtyDaysAgo),
      this.playDataService.getVideoViews(thirtyDaysAgo),
      this.playDataService.getPageViewsByCreator(
        this.currentUser.id,
        thirtyDaysAgo
      ), // new
    ]);

    console.log(pageViews);
    this.processEngagementCharts(subscriptionPayments, sales, pageViews); // modified
    this.processTopItems(streams, videoViews);
    this.cdr.detectChanges();
  }

  async loadConcerts() {
    if (!this.currentUser?.id) return;
    const concerts = await this.playDataService.getConcertsByCreator(
      this.currentUser.id
    );
    const now = new Date();
    this.upcomingConcerts = concerts.filter((c) => new Date(c.date) >= now);
    this.pastConcerts = concerts.filter((c) => new Date(c.date) < now);
    this.cdr.detectChanges();
  }

  openConcertForm(concert: any = null) {
    this.isEditingConcert = !!concert;
    this.initializeConcertForm(concert);
    this.showConcertForm = true;
  }

  cancelConcertEdit() {
    this.showConcertForm = false;
    this.isEditingConcert = false;
    this.concertForm.reset();
  }

  async saveConcert() {
    if (this.concertForm.invalid) {
      return;
    }
    const formValue = this.concertForm.getRawValue();

    // Convert ticket prices from string to number before saving
    if (formValue.ticketTypes && Array.isArray(formValue.ticketTypes)) {
      formValue.ticketTypes.forEach((ticket) => {
        ticket.price = parseFloat(ticket.price) || 0;
      });
    }

    const concertData = {
      ...formValue,
      artistId: this.currentUser.id,
      date: new Date(formValue.date).toISOString(),
    };

    if (this.isEditingConcert) {
      await this.playDataService.updateConcert(concertData.id, concertData);
    } else {
      delete concertData.id;
      await this.playDataService.addConcert(concertData);
    }

    this.cancelConcertEdit();
    this.loadConcerts();
  }

  async deleteConcert(concertId: string) {
    if (confirm("Are you sure you want to delete this concert?")) {
      await this.playDataService.deleteConcert(concertId);
      this.loadConcerts();
    }
  }

  openMembershipForm(membership: any = null, index: number = -1) {
    this.isEditingMembership = !!membership;
    this.editingMembershipIndex = index;

    let initialData = {};
    if (this.isEditingMembership) {
      initialData = membership;
    } else {
      // Determine the next available level automatically
      const existingLevels = (this.currentUser.memberships || []).map(
        (m) => m.level
      );
      const allLevels = ["low", "mid", "hi"];
      const nextLevel = allLevels.find((l) => !existingLevels.includes(l));
      initialData = { level: nextLevel };
    }

    this.initializeMembershipForm(initialData);
    this.showMembershipForm = true;
  }

  cancelMembershipEdit() {
    this.showMembershipForm = false;
    this.isEditingMembership = false;
    this.editingMembershipIndex = null;
    this.membershipForm.reset();
  }

  async saveMembership() {
    if (this.membershipForm.invalid) {
      return;
    }
    const newMembership = this.membershipForm.getRawValue();
    const currentMemberships = [...(this.currentUser.memberships || [])];

    if (this.isEditingMembership && this.editingMembershipIndex !== null) {
      // Update existing membership
      currentMemberships[this.editingMembershipIndex] = newMembership;
    } else {
      // Add new membership
      currentMemberships.push(newMembership);
    }

    try {
      await this.playDataService.updateUserMemberships(
        this.currentUser.id,
        currentMemberships
      );
      this.currentUser.memberships = currentMemberships;
      this.cancelMembershipEdit();
      this.cdr.detectChanges();
    } catch (error) {
      console.error("Error saving membership:", error);
    }
  }

  async deleteMembership(index: number) {
    if (confirm("Are you sure you want to delete this membership tier?")) {
      const currentMemberships = [...(this.currentUser.memberships || [])];
      currentMemberships.splice(index, 1);
      await this.playDataService.updateUserMemberships(
        this.currentUser.id,
        currentMemberships
      );
      this.currentUser.memberships = currentMemberships;
      this.cdr.detectChanges();
    }
  }

  async saveProfile() {
    if (this.profileForm.invalid) {
      return;
    }
    this.handleExistsError = false;
    const formValue = this.profileForm.getRawValue();

    let newAvatarUrl = this.currentUser.avatarUrl || null;
    let newBannerUrl = this.currentUser.bannerPhoto || null;

    // 1. Handle image uploads
    try {
      if (this.selectedAvatarFile) {
        newAvatarUrl = await this.uploadProfileImage(
          this.selectedAvatarFile,
          "avatar"
        );
        this.selectedAvatarFile = null;
        this.avatarPreviewUrl = null;
      }

      if (this.selectedBannerFile) {
        newBannerUrl = await this.uploadProfileImage(
          this.selectedBannerFile,
          "banner"
        );
        this.selectedBannerFile = null;
        this.bannerPreviewUrl = null;
      }
    } catch (error) {
      console.error("Error uploading profile images:", error);
      alert("There was an error uploading your images. Please try again.");
      // Reset progress bars on error
      this.avatarUploadProgress = null;
      this.bannerUploadProgress = null;
      return; // Stop submission on upload failure
    }

    // 2. Handle and validate the 'handle' field
    let handleToSave = formValue.handle.toLowerCase().replace(/\s/g, "");
    if (this.canEditHandle) {
      if (!handleToSave) {
        // Default to display name if handle is cleared
        handleToSave = this.currentUser.displayName
          .toLowerCase()
          .replace(/\s/g, "");
      }

      // Check for uniqueness only if it has changed
      if (handleToSave !== (this.currentUser.handle || "")) {
        const isUnique = await this.playDataService.isHandleUnique(
          handleToSave
        );
        if (!isUnique) {
          this.handleExistsError = true;
          return; // Stop submission
        }
      }
    }

    // 3. Prepare data for Firestore
    const profileData = {
      ...formValue,
      handle: handleToSave,
      socialLinks: formValue.socialLinks,
      avatarUrl: newAvatarUrl,
      bannerPhoto: newBannerUrl,
    };

    // 4. Update Firestore and local user object
    await this.playDataService.updateUserProfile(
      this.currentUser.id,
      profileData
    );
    this.currentUser = { ...this.currentUser, ...profileData };
    this.authService.setUser(this.currentUser); // Update user in auth service and local storage
    alert("Profile saved successfully!");
  }

  copyVanityUrl() {
    navigator.clipboard.writeText("https://" + this.vanityUrl).then(() => {
      alert("URL copied to clipboard!");
    });
  }

  processEngagementCharts(subPayments: any[], sales: any[], pageViews: any[]) {
    // --- New logic for Line Chart ---
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Helper to create a map of counts per day
    const aggregateByDay = (items: any[], dateField: string) => {
      const dailyCounts = new Map<string, number>();
      for (const item of items) {
        if (item[dateField]) {
          // Handle both ISO strings and Firestore Timestamps
          const dateValue =
            item[dateField].toDate instanceof Function
              ? item[dateField].toDate()
              : new Date(item[dateField]);
          const date = dateValue.toISOString().split("T")[0];
          dailyCounts.set(date, (dailyCounts.get(date) || 0) + 1);
        }
      }
      return dailyCounts;
    };

    const dailySubscribers = aggregateByDay(subPayments, "createdAt");
    const dailyPageViews = aggregateByDay(pageViews, "timestamp");

    const subscriberSeries = [];
    const pageViewSeries = [];
    const followerSeries = []; // This will now be cumulative

    let cumulativeFollowers =
      (this.currentUser?.followers || 0) -
      (this.currentUser?.followerGrowthLast30Days || 0);

    for (let i = 0; i <= 30; i++) {
      const date = new Date(thirtyDaysAgo);
      date.setDate(date.getDate() + i);
      const dateString = date.toISOString().split("T")[0];

      subscriberSeries.push({
        name: date,
        value: dailySubscribers.get(dateString) || 0,
      });
      pageViewSeries.push({
        name: date,
        value: dailyPageViews.get(dateString) || 0,
      });
      followerSeries.push({
        name: date, // The date for the point
        value: cumulativeFollowers, // The cumulative value up to that day
      });
    }

    this.subscriberFollowerData = [
      { name: "New Subscribers", series: subscriberSeries },
      { name: "Page Views", series: pageViewSeries },
      { name: "Follower Growth", series: followerSeries },
    ];

    // Calculate revenue from all sources
    const subscriptionRevenue = subPayments.reduce(
      (sum, payment) => sum + (payment.price || 0),
      0
    );

    let merchRevenue = 0;
    let ticketRevenue = 0;
    let musicRevenue = 0; // Corresponds to 'streams'
    let videoRevenue = 0;

    sales.forEach((sale) => {
      const saleItem = sale.item;
      if (saleItem && saleItem.price && saleItem.quantity) {
        const revenue = saleItem.price * saleItem.quantity;
        switch (saleItem.type) {
          case "merch":
            merchRevenue += revenue;
            break;
          case "ticket":
            ticketRevenue += revenue;
            break;
          case "music":
            musicRevenue += revenue;
            break;
          case "video":
            videoRevenue += revenue;
            break;
        }
      }
    });

    const totalRevenue =
      subscriptionRevenue +
      merchRevenue +
      ticketRevenue +
      musicRevenue +
      videoRevenue;

    if (totalRevenue > 0) {
      this.revenueBreakdownData = [
        { name: "Merch", value: merchRevenue },
        { name: "Tickets", value: ticketRevenue },
        { name: "Subscriptions", value: subscriptionRevenue },
        { name: "Streams", value: musicRevenue }, // Using 'Streams' as the label
        { name: "Video", value: videoRevenue },
      ].filter((item) => item.value > 0); // Only show slices with revenue
    }
  }

  processTopItems(streams: any[], views: any[]) {
    // Process Top Songs

    const songPlayCounts = streams.reduce((acc, stream) => {
      acc[stream.track] = (acc[stream.track] || 0) + 1;
      return acc;
    }, {});
    console.log(streams);
    this.topSongs = Object.entries(songPlayCounts)
      .map(([trackId, plays]: [string, number]) => {
        const trackInfo = this.findTrackMetadata(trackId);
        return {
          ...trackInfo,
          plays,
        };
      })
      .filter((song) => song.title) // Filter out songs not found in metadata
      .sort((a, b) => b.plays - a.plays)
      .slice(0, 5);

    // Process Top Videos
    const videoViewCounts = views.reduce((acc, view) => {
      acc[view.videoId] = (acc[view.videoId] || 0) + 1;
      return acc;
    }, {});

    this.topVideos = Object.entries(videoViewCounts)
      .map(([videoId, views]: [string, number]) => {
        const videoInfo = this.findVideoMetadata(videoId);
        return {
          ...videoInfo,
          views,
        };
      })
      .filter((video) => video.title) // Filter out videos not found
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }

  private findTrackMetadata(trackId: string) {
    for (const album of this.albums) {
      const track = album.tracks.find((t) => t.id === trackId);
      if (track) {
        return { ...track, albumArt: album.art };
      }
    }
    return null; // Track not found
  }

  private findVideoMetadata(videoId: string) {
    return this.videos.find((v) => v.id === videoId);
  }

  loadAllStats() {
    // Fetch both music and video stats in parallel
    return Promise.all([this.fetchMusicStats(), this.fetchVideoStats()]);
  }

  async onFileSelected(event: Event, index: number) {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith(".m4a")) {
        alert("Please upload only .m4a files for music.");
        (event.target as HTMLInputElement).value = "";
        this.selectedTrackFile = null;
        return;
      }
      this.selectedTrackFile = file;
    }
  }

  async getDisogData() {
    const result = {};
    const data = await this.playDataService.getStats(); // Fetch the stream data
    // Group songs by album and track

    for (const item of data) {
      const albumId = item.album; // Use album name as the key
      const trackId = item.track; // Use track name as the key
      if (!result[albumId]) {
        result[albumId] = {
          tracks: {}, // Initialize tracks object for the album
        };
      }

      if (!result[albumId].tracks[trackId]) {
        result[albumId].tracks[trackId] = {
          playCount: 0,
          uniqueListeners: new Set(), // Use a Set to track unique users
        };
      }

      // Increment play count for the track
      result[albumId].tracks[trackId].playCount++;

      // Add the user to the uniqueListeners set
      result[albumId].tracks[trackId].uniqueListeners.add(item.user);
    }

    // Convert uniqueListeners Set to a count
    for (const albumId in result) {
      for (const trackId in result[albumId].tracks) {
        const trackData = result[albumId].tracks[trackId];
        trackData.uniqueListenerCount = trackData.uniqueListeners.size; // Convert Set size to count
        delete trackData.uniqueListeners; // Remove the Set to clean up the object
      }
    }
    console.log(result);
    return result; // Return the processed data
  }

  async getProcessedVideoViews() {
    const result = {};
    const data = await this.playDataService.getVideoViews(); // new service method
    if (!data) return {};
    console.log(data);
    for (const item of data) {
      const videoId = item.videoId;
      if (!result[videoId]) {
        result[videoId] = {
          viewCount: 0,
          uniqueViewers: new Set(),
        };
      }
      result[videoId].viewCount++;
      result[videoId].uniqueViewers.add(item.userId);
    }

    for (const videoId in result) {
      const videoData = result[videoId];
      videoData.uniqueViewerCount = videoData.uniqueViewers.size;
      delete videoData.uniqueViewers;
    }
    return result;
  }

  async fetchVideoStats() {
    const creatorId = JSON.parse(localStorage.getItem("user")).id;
    const [videos, processedViews] = await Promise.all([
      this.playDataService.getVideosByCreator(creatorId),
      this.getProcessedVideoViews(),
    ]);

    console.log(videos, processedViews);
    this.videos = videos.map((video) => {
      const videoData = processedViews[video.id];
      return {
        ...video,
        viewCount: videoData?.viewCount || 0,
        uniqueViewerCount: videoData?.uniqueViewerCount || 0,
        editing: false,
        mode: video.showing ? "Active" : "Draft",
        description: video.description || "",
        // Set dspDate to releaseDate if it doesn't exist, for the edit form
        dspDate: video.dspDate || video.releaseDate,
        type: video.type || "Music Video", // Default for existing videos
        price: video.price || 0,
        membersOnly: video.membersOnly || false,
        tier: video.tier || "",
        level: video.level || "",
      };
    });
    this.cdr.detectChanges();
  }

  fetchMusicStats() {
    this.playDataService.getAllDiscography().then((albums) => {
      this.albums = albums.map((album: any) => ({
        ...album,
        editing: false,
        price: album.price || 0,
        membersOnly: album.membersOnly || false,
        tier: album.tier || "",
        level: album.level || "",

        tracks: (album.tracks || []).map((track) => ({
          ...track,
          price: track.price || 0,
          membersOnly: track.membersOnly || false,
          level: track.level || "",
          tier: track.tier || "",
        })),
      }));
      this.getDisogData().then((processedData) => {
        // Merge the processed data into this.albums

        this.albums = this.albums.map((album) => {
          const albumData = processedData[album.id]; // Match by album name

          if (albumData) {
            album.tracks = album.tracks.map((track) => {
              const trackData = albumData.tracks[track.id]; // Match by track title
              if (trackData) {
                // Add playCount and uniqueListenerCount to the track object
                track.playCount = trackData.playCount;
                track.uniqueListenerCount = trackData.uniqueListenerCount;
              } else {
                // Initialize to 0 if no data is found
                track.playCount = 0;
                track.uniqueListenerCount = 0;
              }
              return track;
            });
          }
          return album;
        });
      });
    });
  }

  startEdit(album: any) {
    album.editing = true;
    this.editingAlbumId = album.id;
    this.albumForm.reset(); // Reset the form to clear previous edits
    this.albumForm.patchValue(album); // Fill the form with the album to edit
    // Handle tracks separately if needed
  }

  toCamelCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("");
  }

  mergeAlbumAndDiscogData(
    albums: Album[],
    discogData: { [albumName: string]: ResultAlbum }
  ): Album[] {
    return albums.map((album) => {
      const resultAlbum = discogData[album.name]; // Use discogData instead of result
      if (resultAlbum) {
        album.tracks = album.tracks.map((track) => {
          const resultTrack = resultAlbum.tracks[track.title];
          if (resultTrack) {
            // Merge play count and unique listener count into the track
            track.playCount = resultTrack.playCount;
            track.uniqueListenerCount = resultTrack.uniqueListeners.size;
          } else {
            // Initialize to 0 if no data is found
            track.playCount = 0;
            track.uniqueListenerCount = 0;
          }
          return track;
        });
      }
      return album;
    });
  }

  edit(albumId: string) {
    this.router.navigate(["/upload", albumId]);
  }

  cancelEdit(item: any) {
    item.editing = false;
    // Optionally, you could re-fetch the item's data to discard changes
    console.log("After cancelEdit:", item);
    this.cdr.detectChanges(); // Manually trigger change detection
  }

  saveEdit(album: Album) {
    album.editing = false;
    if (!album.membersOnly) {
      album.tier = "";
      album.level = "";
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tracks, editing, mode, ...albumData } = album;
    this.playDataService.updateAlbum(album.id, albumData);
  }

  saveVideoEdit(video: any) {
    video.editing = false;
    if (!video.membersOnly) {
      video.tier = "";
      video.level = "";
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { editing, mode, viewCount, uniqueViewerCount, ...videoData } = video;
    this.playDataService.updateVideo(video.id, videoData);
  }

  editAudio(album: Album, track: Track): void {
    track.editing = true;
  }

  editTrack(album: Album, track: Track): void {
    track.editing = true;
    this.selectedTrackFile = null; // Reset file on new edit
    console.log(this.albumForm.value);
  }

  onVideoTierChange(video: any, selectedTier: string) {
    if (selectedTier) {
      const member = this.currentUser?.memberships.find(
        (m) => m.tier === selectedTier
      );
      video.level = member?.level || "";
    }
  }

  cancelEditTrack(track: Track): void {
    track.editing = false;
    this.selectedTrackFile = null;
  }

  async saveEditTrack(album: Album, index: number, track: Track) {
    track.editing = false;
    if (!track.membersOnly) {
      track.tier = "";
      track.level = "";
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { editing, playCount, uniqueListenerCount, ...trackData } = track;

    const saveMetadata = (dataToSave: any) => {
      this.playDataService
        .updateTrackInAlbum(album.id, track.id, dataToSave)
        .then(() => {
          this.cdr.detectChanges();
        })
        .catch((err) => console.error("Failed to save track metadata:", err));
    };

    // If a new file was selected, upload it first.
    if (this.selectedTrackFile) {
      const formData = new FormData();
      formData.append("file", this.selectedTrackFile);
      formData.append("uploadType", "track");
      formData.append("creatorId", this.currentUser.id);
      formData.append("albumId", album.id);
      formData.append("trackId", track.id);

      this.http
        .post(`${environment.myBackend}/upload`, formData, {
          reportProgress: true,
          observe: "events",
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              this.upload = Math.round((event.loaded / event.total) * 100);
              this.cdr.detectChanges();
            } else if (event.type === HttpEventType.Response) {
              const res: any = event.body;
              trackData.url = res.url; // Add new URL to metadata
              track.url = res.url; // Update local model
              saveMetadata(trackData); // Save all metadata at once
              this.selectedTrackFile = null;
              this.upload = undefined;
            }
          },
          error: (err) => {
            console.error("Error uploading track:", err);
            this.upload = undefined;
            this.selectedTrackFile = null;
            this.cdr.detectChanges();
          },
        });
    } else {
      // No new file, just update the metadata
      saveMetadata(trackData);
    }
  }

  openSong(artwork: string, track: Track, album: Album) {
    const allStreams = this.playDataService.returnStreams();
    if (allStreams) {
      const songPlays = allStreams.filter(
        (s) => s.album === album.id && s.track === track.id
      );

      const data = {
        name: track.title,
        album: album.name,
        plays: songPlays,
        artWork: artwork,
      };
      this.playDataService.setPlayData(data);

      // Navigate to the songs component with the identifier
      this.router.navigate(["/songs", this.toCamelCase(track.title)]);
    } else {
      console.error(
        "Streams data not available. It might not have been fetched yet."
      );
    }
  }

  private uploadProfileImage(
    file: File,
    type: "avatar" | "banner"
  ): Promise<string> {
    const progressTracker = `${type}UploadProgress`; // e.g., 'avatarUploadProgress'

    return new Promise((resolve, reject) => {
      this[progressTracker] = 0;
      // This now calls a method that uploads to your custom Express backend.
      // This service method should use HttpClient to POST the file as FormData.
      this.playDataService
        .uploadProfileImage(file, type, this.currentUser.id)
        .subscribe({
          next: (response: { url: string }) => {
            this[progressTracker] = 100; // Mark as complete
            this[progressTracker] = null;
            resolve(response.url);
          },
          error: (error) => {
            this[progressTracker] = null;
            console.error(`Error uploading ${type}:`, error);
            reject(error);
          },
        });
    });
  }
}
